import { useState, useCallback } from 'react';
import { post } from '../api';

/**
 * Custom hook to handle retrying a job.
 * 
 * @param {function} onRetrySuccess - Callback function executed after a successful retry. 
 *                                    Receives the updated job data as an argument.
 * @returns {object} An object containing:
 *  - retryingJobs: A Set of job IDs currently being retried.
 *  - retryError: An error message string if the last retry failed, otherwise null.
 *  - triggerRetry: An async function to initiate the retry process for a given job ID.
 */
export function useRetryJob(onRetrySuccess) {
  const [retryingJobs, setRetryingJobs] = useState(new Set());
  const [retryError, setRetryError] = useState(null);

  const triggerRetry = useCallback(async (jobId) => {
    setRetryingJobs((prev) => new Set(prev).add(jobId));
    setRetryError(null);

    try {
      const updatedJobData = await post(`/api/1/jobs/job/${jobId}/retry`);

      if (onRetrySuccess) {
        onRetrySuccess(updatedJobData);
      }

    } catch (error) {
      console.error("Retry failed for job:", jobId, error);
      const errorMessage = error?.data?.error || error?.message || 'Unknown error during retry. Please try again.';
      setRetryError(`Failed to retry job ${jobId.substring(0, 8)}...: ${errorMessage}`);
    } finally {
      setRetryingJobs((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  }, [onRetrySuccess]);

  return { retryingJobs, retryError, triggerRetry };
} 